import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Card, CircularProgress, Table, TableHead, TableBody, TableRow,
  TableCell, Chip, Button, Stack, Typography, Snackbar, Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { getData, putData, API_ENDPOINTS } from "../../services/apiService";
import WeightTierEditDialog from "./WeightTierEditDialog";
import {
  PRC_BG, PRC_PRIMARY_GRADIENT, PRC_PRIMARY_GRADIENT_HOVER, PRC_PRIMARY_SHADOW,
} from "./_pricingTheme";

const fmtRange = (lo, hi) => {
  const loStr = lo == null ? "0" : Number(lo).toString();
  const hiStr = hi == null ? "∞" : Number(hi).toString();
  return `${loStr} – ${hiStr} kg`;
};

const fmtX = (n) => (n == null ? "—" : `×${Number(n).toFixed(2)}`);

export default function WeightTierTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getData(API_ENDPOINTS.pricingWeightTiers);
      setRows(res?.message?.data || res?.result || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const submit = async (id, patch) => {
    const url = API_ENDPOINTS.pricingWeightTierUpdate.replace(":id", id);
    try {
      await putData(url, patch);
      setToast({ kind: "success", message: `Weight tier updated` });
      setEditing(null);
      fetchRows();
      return { ok: true };
    } catch (err) {
      const data = err?.response?.data;
      const message = data?.message || err.message;
      const errorCode = data?.errorCode || null;
      setToast({ kind: "error", message });
      return { ok: false, message, errorCode };
    }
  };

  const fullTruckRow = useMemo(() => rows.find((r) => r.is_full_truck), [rows]);
  const partialRows = useMemo(() => rows.filter((r) => !r.is_full_truck), [rows]);

  if (loading) {
    return <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>;
  }
  if (error) {
    return (
      <Card elevation={0} sx={{ border: "1px solid #fca5a5", bgcolor: "#fee2e2", p: 2 }}>
        <Typography color="error">Weight tiers failed: {error}</Typography>
      </Card>
    );
  }

  return (
    <Box>
      {/* Info strip */}
      <Box
        sx={{
          mb: 2, p: 2, borderRadius: 2.5,
          border: "1px solid #80CCCC", bgcolor: "#E0F4F5",
          display: "flex", alignItems: "center", gap: 1.25,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#007C84" }} />
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#016268", fontSize: 13.5 }}>
            {partialRows.length} partial-truck band{partialRows.length === 1 ? "" : "s"}
            {fullTruckRow ? " · 1 full-truck row" : ""}
          </Typography>
          <Typography variant="caption" sx={{ color: "#007C84" }}>
            <code>multiplier_point</code> must satisfy <code>multiplier_min ≤ point ≤ multiplier_max</code>. Server rejects out-of-range edits.
          </Typography>
        </Box>
      </Box>

      <Card elevation={0} sx={{ border: `1px solid ${PRC_BG.cardBorder}`, borderRadius: 2.5, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  bgcolor: "#F0FDFA",
                  fontWeight: 800,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "#0F766E",
                },
              }}
            >
              <TableCell>Tier</TableCell>
              <TableCell>Weight band</TableCell>
              <TableCell>Multiplier range</TableCell>
              <TableCell>Active point</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partialRows.map((row) => (
              <TableRow key={row.id} hover sx={{ "&:hover": { bgcolor: PRC_BG.rowHover } }}>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                  <Typography fontWeight={700} sx={{ color: "#0F172A" }}>{row.tier_name}</Typography>
                  <Typography variant="caption" sx={{ color: "#64748B", fontFamily: "monospace", display: "block", mt: 0.25 }}>
                    {row.tier_code}
                  </Typography>
                  {row.description && (
                    <Typography variant="body2" sx={{ color: "#475569", mt: 0.75, lineHeight: 1.45, maxWidth: 360 }}>
                      {row.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, fontFamily: "monospace", color: "#334155" }}>
                  {fmtRange(row.min_weight_kg, row.max_weight_kg)}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                  <Typography sx={{ fontFamily: "monospace", color: "#475569", fontSize: 13 }}>
                    {fmtX(row.multiplier_min)} <span style={{ opacity: 0.5 }}>→</span> {fmtX(row.multiplier_max)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                  <Chip
                    label={fmtX(row.multiplier_point)}
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 800,
                      height: 28,
                      background: "linear-gradient(135deg, #DCFCE7, #BBF7D0)",
                      color: "#065F46",
                      border: "1px solid #6EE7B7",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, whiteSpace: "nowrap", fontSize: 11, color: "#64748B" }}>
                  <div>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "—"}</div>
                  <div style={{ fontFamily: "monospace", marginTop: 2 }}>
                    {row.updated_by_admin_id || <span style={{ opacity: 0.6 }}>(seed)</span>}
                  </div>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }} align="right">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => setEditing(row)}
                    sx={{
                      background: PRC_PRIMARY_GRADIENT,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: PRC_PRIMARY_SHADOW,
                      "&:hover": { background: PRC_PRIMARY_GRADIENT_HOVER },
                    }}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {fullTruckRow && (
              <TableRow
                hover
                sx={{
                  bgcolor: PRC_BG.fullTruck,
                  "&:hover": { bgcolor: "#FDE68A" },
                  borderTop: `2px solid ${PRC_BG.fullTruckBorder}`,
                }}
              >
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocalShippingIcon sx={{ color: "#B45309", fontSize: 18 }} />
                    <Typography fontWeight={700} sx={{ color: "#78350F" }}>
                      {fullTruckRow.tier_name}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#92400E", fontFamily: "monospace", display: "block", mt: 0.25 }}>
                    {fullTruckRow.tier_code}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#78350F", mt: 0.75, lineHeight: 1.45, maxWidth: 360 }}>
                    {fullTruckRow.description}
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, fontFamily: "monospace", color: "#92400E" }}>
                  (any)
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, color: "#92400E" }}>
                  N/A — multiplier path skipped
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, fontFamily: "monospace", color: "#78350F" }}>
                  preFee = trip_cost + handling
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, whiteSpace: "nowrap", fontSize: 11, color: "#92400E" }}>
                  <div>{fullTruckRow.updatedAt ? new Date(fullTruckRow.updatedAt).toLocaleString() : "—"}</div>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }} align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => setEditing(fullTruckRow)}
                    sx={{ textTransform: "none", fontWeight: 700, borderColor: "#B45309", color: "#92400E" }}
                  >
                    Edit metadata
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <WeightTierEditDialog
        item={editing}
        onClose={() => setEditing(null)}
        onSubmit={submit}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast?.kind || "success"} onClose={() => setToast(null)} sx={{ fontWeight: 600 }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
